import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { supabase } from '../utils/supabaseClient';
import { KeyIcon, CheckCircleIcon, XCircleIcon, EyeIcon, EyeSlashIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { showToast } from '../components/Common/Toast';
import BrandedLogo from '../components/Common/BrandedLogo';
import { useBrand } from '../contexts/BrandContext';

const SetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateUser, loading: userLoading } = useUser();
  const { brand } = useBrand();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // Capture the password-recovery signal SYNCHRONOUSLY on first render, before
  // supabase-js consumes the URL hash. Supabase redirects recovery links to
  // `${redirectTo}#access_token=...&type=recovery`, so `type=recovery` is the
  // reliable "this is a forgot-password reset" marker (independent of whether
  // UserContext has loaded a user yet).
  const [isPasswordReset, setIsPasswordReset] = useState(() => {
    if (typeof window === 'undefined') return false;
    const h = window.location.hash || '';
    const s = window.location.search || '';
    return h.includes('type=recovery') || s.includes('type=recovery');
  });
  // Gate ALL redirects until the async session check finishes — otherwise the
  // page bounces a valid reset to /login before it knows it's a reset flow.
  const [checking, setChecking] = useState(true);
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: 'Very Weak', feedback: [] });

  // Decide why we're here (reset vs must-change-password vs not-allowed) and
  // only redirect once the async session check is done — never before, or a
  // valid reset gets bounced to /login. Recovery is already detected
  // synchronously above; a session-without-user is also treated as a reset.
  useEffect(() => {
    let cancelled = false;
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;

      /**
       * Board #3200. `!user && session` used to be enough to enter reset mode, and
       * UserContext resolves `user` ASYNCHRONOUSLY — so an already-logged-in person who
       * navigated straight to /set-password hit this while `user` was still null and
       * latched isPasswordReset = true. The effect re-runs when `user` arrives, but the
       * first branch matches again on the flag it just set, so it never un-latches: an
       * authenticated session got a no-token password change, which is exactly what the
       * finding describes.
       *
       * Waiting for `userLoading` to settle removes the race. An explicit recovery marker
       * in the URL still enters reset mode immediately, because that is a genuine
       * no-user-yet flow and must not depend on UserContext at all.
       */
      if (isPasswordReset) {
        setChecking(false);
        return;
      }

      if (userLoading) return; // not enough information yet — decide on the next run

      if (!user && session) {
        setIsPasswordReset(true);
        setChecking(false);
        return;
      }

      if (user) {
        if (user.must_change_password === false) {
          // Same reason as the post-submit redirect below: '/' lets DashboardRouter
          // pick by role, so a non-admin who reaches this page with a password already
          // set is not bounced to /unauthorized.
          navigate('/');
        }
        setChecking(false);
        return;
      }

      // No reset marker, no session, no user → not allowed here.
      navigate('/login');
      setChecking(false);
    };
    checkSession();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, userLoading]);

  // Calculate password strength
  useEffect(() => {
    const password = formData.newPassword;
    let score = 0;
    const feedback = [];

    // Length
    if (password.length >= 8) score += 1;
    if (password.length >= 12) score += 1;
    if (password.length >= 16) score += 1;
    else if (password.length > 0) feedback.push('Use at least 12 characters for better security');

    // Character variety
    if (/[a-z]/.test(password)) score += 1;
    else if (password.length > 0) feedback.push('Add lowercase letters');

    if (/[A-Z]/.test(password)) score += 1;
    else if (password.length > 0) feedback.push('Add uppercase letters');

    if (/[0-9]/.test(password)) score += 1;
    else if (password.length > 0) feedback.push('Add numbers');

    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
    else if (password.length > 0) feedback.push('Add special characters');

    // Common patterns (reduce score)
    if (/^[0-9]+$/.test(password)) {
      score -= 2;
      feedback.push('Avoid using only numbers');
    }
    if (/^[a-zA-Z]+$/.test(password)) {
      score -= 1;
      feedback.push('Mix letters with numbers and symbols');
    }
    if (/(.)\1{2,}/.test(password)) {
      score -= 1;
      feedback.push('Avoid repeating characters');
    }

    // Common words
    const commonWords = ['password', 'admin', 'user', '1234', 'qwerty'];
    if (commonWords.some(word => password.toLowerCase().includes(word))) {
      score -= 2;
      feedback.push('Avoid common words');
    }

    const strength = Math.max(0, Math.min(5, score));
    const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong', 'Very Strong'];

    setPasswordStrength({
      score: strength,
      label: labels[strength],
      feedback
    });
  }, [formData.newPassword]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (formData.newPassword !== formData.confirmPassword) {
      showToast('error', 'Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      showToast('error', 'Password must be at least 8 characters long');
      return;
    }

    if (!isPasswordReset && passwordStrength.score < 2) {
      showToast('error', 'Please choose a stronger password');
      return;
    }

    setLoading(true);

    try {
      console.log('🔐 Setting new password...');

      // Update password using Supabase Auth
      const { error: updateError } = await supabase.auth.updateUser({
        password: formData.newPassword
      });

      if (updateError) {
        console.error('❌ Password update error:', updateError);
        throw new Error(updateError.message);
      }

      console.log('✅ Password updated successfully in Auth');

      // If this is a must_change_password flow (logged in user), update the flag
      if (user && user.user_id) {
        // Update the users table to clear the must_change_password flag
        const { error: dbError } = await supabase
          .from('users')
          .update({
            must_change_password: false,
            updated_at: new Date().toISOString()
          })
          .eq('id', user.user_id);

        if (dbError) {
          console.warn('⚠️ Failed to update must_change_password flag:', dbError);
        } else {
          console.log('✅ must_change_password flag cleared');
        }

        // Update local user state
        updateUser({
          ...user,
          must_change_password: false
        });

        showToast('success', '✅ Password set successfully! Redirecting to dashboard...');

        // '/' — NOT '/admin-dashboard'. This is the last step of accepting a team
        // INVITE, and most invitees are not admins. AdminDashboard guards on
        // ['APP_OWNER','OWNER','ADMIN'] and redirects everyone else to /unauthorized,
        // so an invited technician finished setting their password and landed on an
        // error page. '/' is DashboardRouter, which already decides by role
        // (isAdmin || isOwner -> AdminDashboard, otherwise MyDashboard) and is the
        // canonical owner of "where does this user belong". Sending crew straight to
        // their own work is also the industry-standard invite landing.
        setTimeout(() => {
          navigate('/');
        }, 1500);
      } else {
        // Password reset flow - redirect to login
        showToast('success', '✅ Password reset successfully! Redirecting to login...');
        
        // Sign out and redirect to login
        await supabase.auth.signOut();
        setTimeout(() => {
          navigate('/login');
        }, 1500);
      }

    } catch (error) {
      console.error('❌ Error setting password:', error);
      showToast('error', `Failed to set password: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getStrengthColor = (score) => {
    const colors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-lime-500', 'bg-green-500', 'bg-emerald-500'];
    return colors[score] || 'bg-gray-300';
  };

  // While the session check runs, show a loader — never prematurely return null
  // or redirect (that's what bounced valid resets to /login).
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Render the form for a reset flow (recovery session, no app user) OR a
  // logged-in must-change-password user. Otherwise a redirect was already issued.
  if (!isPasswordReset && (!user || user.must_change_password === false)) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          <BrandedLogo className="h-12 w-auto mx-auto mb-4" showText={true} />
          <div className="flex items-center justify-center gap-3 mb-4">
            <ShieldCheckIcon className="w-10 h-10 text-yellow-500" />
          </div>
          {/* Board #1971: opened at <h2> with no <h1> on the page, like Login and Forgot
              password. Tag only — the visual classes are untouched. */}
          <h1 className="text-2xl font-bold text-white">
            {isPasswordReset ? 'Reset Your Password' : 'Set Your Password'}
          </h1>
          <p className="mt-2 text-sm text-gray-300">
            {isPasswordReset 
              ? 'Enter a new password for your account.' 
              : `Welcome, ${user?.first_name || 'there'}! Please create a new password for your account.`}
          </p>
          <p className="mt-1 text-xs text-gray-400">
            {isPasswordReset 
              ? 'Choose a strong password you\'ll remember.' 
              : 'Your temporary password has been replaced. Create a strong password you\'ll remember.'}
          </p>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="py-8 px-4 sm:px-10 rounded-2xl backdrop-blur-xl border border-white/10 shadow-2xl" style={{
          background: "rgba(30, 41, 59, 0.7)",
          backdropFilter: "blur(15px)"
        }}>
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* New Password */}
            <div>
              <label htmlFor="newPassword" className="block text-sm font-medium text-gray-200">
                New Password *
              </label>
              <div className="mt-1 relative">
                <input
                  id="newPassword"
                  name="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.newPassword}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 pr-10 bg-slate-700/50 border border-slate-600/50 rounded-md placeholder-gray-500 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>

              {/* Password Strength Indicator */}
              {formData.newPassword && (
                <div className="mt-2">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex-1 h-2 bg-gray-700 rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${getStrengthColor(passwordStrength.score)}`}
                        style={{ width: `${(passwordStrength.score + 1) * 16.67}%` }}
                      />
                    </div>
                    <span className={`text-xs font-medium ${
                      passwordStrength.score < 2 ? 'text-red-400' :
                      passwordStrength.score < 4 ? 'text-yellow-400' :
                      'text-green-400'
                    }`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  {passwordStrength.feedback.length > 0 && (
                    <ul className="text-xs text-gray-400 space-y-1">
                      {passwordStrength.feedback.slice(0, 3).map((item, index) => (
                        <li key={index}>• {item}</li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-200">
                Confirm New Password *
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="appearance-none block w-full px-3 py-2 pr-10 bg-slate-700/50 border border-slate-600/50 rounded-md placeholder-gray-500 text-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Confirm your new password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  aria-label={showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'}
                  aria-pressed={showConfirmPassword}
                  title={showConfirmPassword ? 'Hide password confirmation' : 'Show password confirmation'}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                  ) : (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>

              {/* Password Match Indicator */}
              {formData.confirmPassword && (
                <div className="mt-2 flex items-center gap-2">
                  {formData.newPassword === formData.confirmPassword ? (
                    <>
                      <CheckCircleIcon className="w-4 h-4 text-green-500" />
                      <span className="text-xs text-green-400">Passwords match</span>
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="w-4 h-4 text-red-500" />
                      <span className="text-xs text-red-400">Passwords do not match</span>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Password Requirements */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3">
              <p className="text-xs font-medium text-gray-300 mb-2">Password Requirements:</p>
              <ul className="text-xs text-gray-400 space-y-1">
                <li className={formData.newPassword.length >= 8 ? 'text-green-400' : ''}>
                  • At least 8 characters (12+ recommended)
                </li>
                <li className={/[A-Z]/.test(formData.newPassword) ? 'text-green-400' : ''}>
                  • At least one uppercase letter
                </li>
                <li className={/[a-z]/.test(formData.newPassword) ? 'text-green-400' : ''}>
                  • At least one lowercase letter
                </li>
                <li className={/[0-9]/.test(formData.newPassword) ? 'text-green-400' : ''}>
                  • At least one number
                </li>
                <li className={/[!@#$%^&*(),.?":{}|<>]/.test(formData.newPassword) ? 'text-green-400' : ''}>
                  • At least one special character
                </li>
              </ul>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || passwordStrength.score < 2 || formData.newPassword !== formData.confirmPassword}
                className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Setting Password...
                  </>
                ) : (
                  <>
                    <KeyIcon className="h-5 w-5 mr-2" />
                    Set Password & Continue
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
